import { Upload, Sparkles } from "lucide-react";

// Three exclusive states for the upload zone, returned by selectState().
// Switch in JSX keeps the upload zone readable (replaces 3-level nested ternaries).
function selectState({ previewing, imagePreview, imageBase64, cadFormat }) {
  if (previewing) return "converting";
  if (imagePreview) return "image";
  if (imageBase64 && cadFormat) return "cad-pending";
  return "empty";
}

export default function CadUploadZone({
  t, previewing, imagePreview, imageBase64, cadFormat,
  onDrop, fileRef,
}) {
  const state = selectState({ previewing, imagePreview, imageBase64, cadFormat });

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      onClick={() => fileRef.current?.click()}
      className="border-2 border-dashed border-slate-700 rounded-lg p-10 text-center cursor-pointer hover:border-blue-500/60 hover:bg-blue-500/5 transition-colors"
      data-testid="upload-zone"
    >
      {state === "converting" && (
        <div className="space-y-3 py-6">
          <div className="w-14 h-14 mx-auto rounded-full bg-cyan-500/10 flex items-center justify-center mb-2 pulse-blue">
            <Sparkles size={22} className="text-cyan-400 animate-pulse"/>
          </div>
          <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-cyan-300">
            {t("newQuote.convertingCad", { defaultValue: `Conversione ${cadFormat?.toUpperCase() || "CAD"} in corso…` })}
          </div>
          <div className="max-w-sm mx-auto h-1 bg-slate-800 rounded overflow-hidden">
            <div className="h-full bg-cyan-500 animate-pulse" style={{ width: "70%" }} />
          </div>
        </div>
      )}

      {state === "image" && (
        <div className="space-y-3">
          <img src={imagePreview} alt="preview" className="max-h-72 mx-auto rounded border border-slate-700 bg-white p-2"/>
          {cadFormat && !["png", "jpeg", "jpg", "webp"].includes(cadFormat) && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded border border-cyan-500/40 bg-cyan-500/10 font-mono text-[10px] tracking-wider uppercase text-cyan-300">
              {cadFormat.toUpperCase()} · {t("newQuote.cadConverted", { defaultValue: "convertito server-side" })}
            </div>
          )}
          <div className="text-sm text-slate-400">{t("newQuote.replaceImage")}</div>
        </div>
      )}

      {state === "cad-pending" && (
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded border border-cyan-500/40 bg-cyan-500/10 font-mono text-xs tracking-wider uppercase text-cyan-300">
            {cadFormat.toUpperCase()} caricato · verrà convertito server-side
          </div>
          <div className="text-sm text-slate-400">{t("newQuote.replaceImage")}</div>
        </div>
      )}

      {state === "empty" && (
        <>
          <div className="w-14 h-14 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center mb-3">
            <Upload size={22} className="text-blue-400"/>
          </div>
          <div className="font-display font-medium text-slate-100">{t("newQuote.dropOrClick")}</div>
          <div className="text-slate-500 text-sm mt-1">{t("newQuote.dropSubtitle")}</div>
          <div className="mt-3 font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">PNG · JPEG · WEBP · PDF · DXF</div>
          <div className="mt-1 text-[11px] text-slate-500">{t("newQuote.dwgHint", { defaultValue: "Per DWG: esporta come DXF o PDF dal tuo CAD." })}</div>
        </>
      )}
    </div>
  );
}
